import { JRCEllipsis } from 'components/JRCEllipsis/JRCEllipsis';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import React, { MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { getColor } from 'styles/theme';
import { modifyColorBrightness } from 'styles/utils';
import { DataCy } from '../../types/dataAttributes';

const PublishButtonItem = styled.li`
    height: 48px;
    margin: 2px;
    border-radius: 8px;
    padding: 8px;
    display: flex;
    &:hover {
        background: rgba(115, 100, 227, 0.08);
    }
    &:focus-within {
        background: rgba(115, 100, 227, 0.08);
        border: ${(props) => getColor(props.theme, 'primary')} 1px solid;
    }
`;

const FavoriteButton = styled.button`
    border-radius: 50%;
    &:focus {
        border: ${(props) => getColor(props.theme, 'primary')} 1px solid;
    }
`;

const PublishButtonItemButton = styled.button`
    flex: 1;
    display: flex;
    gap: 4px;
    justify-content: flex-start;
    align-items: center;
`;

const PublishButtonItemLink = styled.a`
    flex: 1;
    display: flex;
    gap: 4px;
    justify-content: flex-start;
    align-items: center;
    &:visited,
    &:focus,
    &:hover {
        border: none;
        text-decoration: none;
        color: inherit;
        outline: none;
    }
`;

export type PublishButtonEntryProps = DataCy & {
    key: string;
    icon: string;
    color: string;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    url?: string;
    label: string;
    isFavorite: boolean;
    favoriteClick: MouseEventHandler<HTMLButtonElement>;
};

export type PublishButtonExtraProps = {
    hasFavorites?: boolean;
};

export const PublishButtonEntry = (props: PublishButtonEntryProps & PublishButtonExtraProps) => {
    const intl = useIntl();
    const iconColor = modifyColorBrightness(props.color, -0.4) ?? '#FFF';
    const bgColor = modifyColorBrightness(props.color, 0.8) ?? '#000';
    return (
        <PublishButtonItem>
            {props.onClick && (
                <PublishButtonItemButton
                    onClick={props.onClick}
                    aria-label={intl.formatMessage({ id: props.label })}
                    data-cy={props.dataCy}>
                    <JRCIcon
                        role="presentation"
                        size={32}
                        variant="circle"
                        color={bgColor}
                        iconColor={iconColor}
                        name={props.icon}
                    />
                    <JRCEllipsis length={20} label={intl.formatMessage({ id: props.label })} />
                </PublishButtonItemButton>
            )}
            {props.url && (
                <PublishButtonItemLink href={props.url} data-cy={props.dataCy}>
                    <JRCIcon
                        role="presentation"
                        size={32}
                        variant="circle"
                        color={bgColor}
                        iconColor={iconColor}
                        name={props.icon}
                    />
                    <JRCEllipsis length={20} label={intl.formatMessage({ id: props.label })} />
                </PublishButtonItemLink>
            )}
            {props.hasFavorites && (
                <FavoriteButton
                    aria-label={
                        props.isFavorite
                            ? intl.formatMessage({ id: 'REMOVE_FAVORITE' })
                            : intl.formatMessage({ id: 'ADD_FAVORITE' })
                    }
                    onClick={props.favoriteClick}>
                    <JRCIcon role="presentation" name={props.isFavorite ? 'icon-star' : 'icon-star-o'} />
                </FavoriteButton>
            )}
        </PublishButtonItem>
    );
};
